import { useMemo } from "react"
import Fuse from "fuse.js"

import type { SearchableFilterItem } from "./types"
import { dedupeById } from "./dedupe-by-id"

export function useFilteredSearchItems<T extends string>(
	globalQuery: string,
	allItems: SearchableFilterItem<T>[],
	options?: {
		categoryOrder?: T[]
		extraItems?: (query: string) => SearchableFilterItem<T>[]
		limit?: number
	},
): SearchableFilterItem<T>[] {
	const { categoryOrder, extraItems, limit = 15 } = options ?? {}

	return useMemo(() => {
		if (!globalQuery) return []

		const nonCategoryItems = dedupeById(allItems).filter(
			(item) => !item.id.startsWith("category-"),
		)

		const fuse = new Fuse(nonCategoryItems, {
			keys: [
				{ name: "label", weight: 2 },
				{ name: "searchText", weight: 1 },
			],
			threshold: 0.4,
		})

		let results = fuse.search(globalQuery).map((r) => r.item)

		if (extraItems) {
			results.push(...extraItems(globalQuery))
			results = dedupeById(results)
		}

		if (categoryOrder && categoryOrder.length > 0) {
			results.sort(
				(a, b) =>
					categoryOrder.indexOf(a.category) - categoryOrder.indexOf(b.category),
			)
		}

		return results.slice(0, limit)
	}, [globalQuery, allItems, categoryOrder, extraItems, limit])
}
